"use client";

import { useState, useMemo } from "react";

interface FearGreedEntry {
  value: string;
  value_classification: string;
  timestamp: string;
}

interface FearGreedClientProps {
  data: FearGreedEntry[] | null;
}

function getColor(value: number): string {
  if (value <= 25) return "#ef4444"; // Extreme Fear — red
  if (value <= 45) return "#f97316"; // Fear — orange
  if (value <= 55) return "#eab308"; // Neutral — yellow
  if (value <= 75) return "#84cc16"; // Greed — lime
  return "#22c55e"; // Extreme Greed — green
}

function getLabel(value: number): string {
  if (value <= 25) return "Extreme Fear";
  if (value <= 45) return "Fear";
  if (value <= 55) return "Neutral";
  if (value <= 75) return "Greed";
  return "Extreme Greed";
}

function GaugeChart({ value }: { value: number }) {
  // SVG semi-circle gauge
  const radius = 120;
  const cx = 150;
  const cy = 140;
  const startAngle = Math.PI; // 180 degrees (left)
  const endAngle = 0; // 0 degrees (right)
  const angle = startAngle - (value / 100) * Math.PI;

  const needleX = cx + radius * 0.85 * Math.cos(angle);
  const needleY = cy - radius * 0.85 * Math.sin(angle);

  // Create gradient arc segments
  const segments = [
    { start: 0, end: 25, color: "#ef4444" },
    { start: 25, end: 45, color: "#f97316" },
    { start: 45, end: 55, color: "#eab308" },
    { start: 55, end: 75, color: "#84cc16" },
    { start: 75, end: 100, color: "#22c55e" },
  ];

  return (
    <svg viewBox="0 0 300 180" className="w-full max-w-md mx-auto">
      {/* Background arc */}
      <path
        d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
        fill="none"
        stroke="rgba(255,255,255,0.05)"
        strokeWidth="24"
        strokeLinecap="round"
      />

      {/* Colored segments */}
      {segments.map((seg) => {
        const s = Math.PI - (seg.start / 100) * Math.PI;
        const e = Math.PI - (seg.end / 100) * Math.PI;
        const x1 = cx + radius * Math.cos(s);
        const y1 = cy - radius * Math.sin(s);
        const x2 = cx + radius * Math.cos(e);
        const y2 = cy - radius * Math.sin(e);
        const largeArc = seg.end - seg.start > 50 ? 1 : 0;
        return (
          <path
            key={seg.start}
            d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`}
            fill="none"
            stroke={seg.color}
            strokeWidth="24"
            strokeLinecap="butt"
            opacity={0.6}
          />
        );
      })}

      {/* Needle */}
      <line
        x1={cx}
        y1={cy}
        x2={needleX}
        y2={needleY}
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx={cx} cy={cy} r="6" fill="white" />

      {/* Value text */}
      <text
        x={cx}
        y={cy - 30}
        textAnchor="middle"
        fill={getColor(value)}
        fontSize="42"
        fontWeight="bold"
        fontFamily="monospace"
      >
        {value}
      </text>
      <text
        x={cx}
        y={cy - 5}
        textAnchor="middle"
        fill="rgba(255,255,255,0.6)"
        fontSize="14"
        fontWeight="500"
      >
        {getLabel(value)}
      </text>

      {/* Labels */}
      <text x={cx - radius - 5} y={cy + 20} textAnchor="middle" fill="#ef4444" fontSize="11" fontWeight="600">
        0
      </text>
      <text x={cx + radius + 5} y={cy + 20} textAnchor="middle" fill="#22c55e" fontSize="11" fontWeight="600">
        100
      </text>
      <text x={cx - radius + 15} y={cy + 20} fill="rgba(255,255,255,0.3)" fontSize="9">
        Fear
      </text>
      <text x={cx + radius - 35} y={cy + 20} fill="rgba(255,255,255,0.3)" fontSize="9">
        Greed
      </text>
    </svg>
  );
}

type TimeRange = "7" | "30" | "90" | "365";

export function FearGreedClient({ data }: FearGreedClientProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>("30");

  const current = data?.[0];
  const currentValue = current ? parseInt(current.value, 10) : 50;

  // Historical stats
  const stats = useMemo(() => {
    if (!data) return null;
    const periods = [
      { label: "Now", days: 1 },
      { label: "Yesterday", days: 2 },
      { label: "Last Week", days: 7 },
      { label: "Last Month", days: 30 },
    ];
    return periods.map((p) => {
      const entry = data[Math.min(p.days - 1, data.length - 1)];
      const val = entry ? parseInt(entry.value, 10) : 0;
      return { ...p, value: val, classification: entry?.value_classification ?? "" };
    });
  }, [data]);

  // Chart data based on time range
  const chartData = useMemo(() => {
    if (!data) return [];
    const limit = parseInt(timeRange, 10);
    return data
      .slice(0, limit)
      .reverse()
      .map((d) => ({
        date: new Date(parseInt(d.timestamp, 10) * 1000),
        value: parseInt(d.value, 10),
        label: d.value_classification,
      }));
  }, [data, timeRange]);

  // Chart dimensions
  const chartWidth = 800;
  const chartHeight = 200;
  const padding = { top: 10, right: 10, bottom: 30, left: 40 };
  const innerW = chartWidth - padding.left - padding.right;
  const innerH = chartHeight - padding.top - padding.bottom;

  // Build SVG path
  const points = chartData.map((d, i) => ({
    x: padding.left + (i / Math.max(chartData.length - 1, 1)) * innerW,
    y: padding.top + (1 - d.value / 100) * innerH,
    value: d.value,
    date: d.date,
  }));

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = linePath + ` L ${points[points.length - 1]?.x ?? 0} ${padding.top + innerH} L ${padding.left} ${padding.top + innerH} Z`;

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-400 text-lg">Unable to load Fear & Greed data. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Crypto Fear & Greed Index</h1>
        <p className="text-gray-400 mt-2">
          A multi-factor sentiment index measuring fear and greed in the crypto market.
          Values range from 0 (Extreme Fear) to 100 (Extreme Greed).
        </p>
      </div>

      {/* Main Gauge + Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gauge */}
        <div className="lg:col-span-1 rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6 flex flex-col items-center justify-center">
          <GaugeChart value={currentValue} />
          <p className="text-xs text-gray-500 mt-4">
            Updated {current ? new Date(parseInt(current.timestamp, 10) * 1000).toLocaleDateString() : "--"}
          </p>
        </div>

        {/* Historical values */}
        <div className="lg:col-span-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Historical Values</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {stats?.map((s) => (
              <div key={s.label} className="bg-[hsl(230,15%,10%)] rounded-lg p-4 text-center">
                <p className="text-xs text-gray-400 mb-2">{s.label}</p>
                <p
                  className="text-3xl font-bold font-mono"
                  style={{ color: getColor(s.value) }}
                >
                  {s.value}
                </p>
                <p className="text-xs mt-1" style={{ color: getColor(s.value) }}>
                  {s.classification}
                </p>
              </div>
            ))}
          </div>

          {/* What does this mean? */}
          <div className="mt-6 grid grid-cols-5 gap-2">
            {[
              { range: "0-25", label: "Extreme Fear", color: "#ef4444", desc: "Investors very worried" },
              { range: "26-45", label: "Fear", color: "#f97316", desc: "Market uncertainty" },
              { range: "46-55", label: "Neutral", color: "#eab308", desc: "Balanced sentiment" },
              { range: "56-75", label: "Greed", color: "#84cc16", desc: "Optimistic market" },
              { range: "76-100", label: "Extreme Greed", color: "#22c55e", desc: "Market euphoria" },
            ].map((z) => (
              <div key={z.range} className="text-center">
                <div
                  className="h-2 rounded-full mb-2"
                  style={{ backgroundColor: z.color, opacity: 0.6 }}
                />
                <p className="text-[10px] font-semibold" style={{ color: z.color }}>
                  {z.label}
                </p>
                <p className="text-[10px] text-gray-500">{z.range}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Index Over Time</h2>
          <div className="flex rounded-lg overflow-hidden border border-[hsl(230,15%,20%)]">
            {(
              [
                { label: "7D", value: "7" },
                { label: "30D", value: "30" },
                { label: "90D", value: "90" },
                { label: "1Y", value: "365" },
              ] as { label: string; value: TimeRange }[]
            ).map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTimeRange(opt.value)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  timeRange === opt.value
                    ? "bg-blue-600 text-white"
                    : "bg-[hsl(230,15%,12%)] text-gray-400 hover:text-white"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* SVG Chart */}
        <div className="overflow-x-auto">
          <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full min-w-[500px]" preserveAspectRatio="none">
            <defs>
              <linearGradient id="fgGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#eab308" stopOpacity="0.1" />
                <stop offset="100%" stopColor="#ef4444" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="fgLineGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#8b5cf6" />
              </linearGradient>
            </defs>

            {/* Grid lines */}
            {[0, 25, 50, 75, 100].map((v) => {
              const y = padding.top + (1 - v / 100) * innerH;
              return (
                <g key={v}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={chartWidth - padding.right}
                    y2={y}
                    stroke="rgba(255,255,255,0.06)"
                    strokeDasharray="4 4"
                  />
                  <text
                    x={padding.left - 8}
                    y={y + 4}
                    textAnchor="end"
                    fill="rgba(255,255,255,0.3)"
                    fontSize="10"
                  >
                    {v}
                  </text>
                </g>
              );
            })}

            {/* Fear/Greed zones */}
            <rect
              x={padding.left}
              y={padding.top}
              width={innerW}
              height={innerH * 0.25}
              fill="rgba(34,197,94,0.04)"
            />
            <rect
              x={padding.left}
              y={padding.top + innerH * 0.75}
              width={innerW}
              height={innerH * 0.25}
              fill="rgba(239,68,68,0.04)"
            />

            {/* 50 line (neutral) */}
            <line
              x1={padding.left}
              y1={padding.top + innerH * 0.5}
              x2={chartWidth - padding.right}
              y2={padding.top + innerH * 0.5}
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="1"
            />

            {/* Area fill */}
            {points.length > 1 && (
              <path d={areaPath} fill="url(#fgGradient)" opacity="0.5" />
            )}

            {/* Line */}
            {points.length > 1 && (
              <path
                d={linePath}
                fill="none"
                stroke="url(#fgLineGradient)"
                strokeWidth="2"
              />
            )}

            {/* Data points (sampled for performance) */}
            {points
              .filter((_, i) => i % Math.max(1, Math.floor(points.length / 30)) === 0 || i === points.length - 1)
              .map((p, i) => (
                <circle
                  key={i}
                  cx={p.x}
                  cy={p.y}
                  r="3"
                  fill={getColor(p.value)}
                  stroke="rgba(0,0,0,0.5)"
                  strokeWidth="1"
                />
              ))}

            {/* X-axis date labels */}
            {points
              .filter((_, i) => {
                const step = Math.max(1, Math.floor(points.length / 6));
                return i % step === 0 || i === points.length - 1;
              })
              .map((p, i) => (
                <text
                  key={i}
                  x={p.x}
                  y={chartHeight - 5}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.3)"
                  fontSize="9"
                >
                  {p.date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </text>
              ))}
          </svg>
        </div>
      </div>

      {/* About section */}
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6">
        <h2 className="text-lg font-semibold text-white mb-3">About the Fear & Greed Index</h2>
        <p className="text-sm text-gray-400 leading-relaxed">
          The Crypto Fear & Greed Index analyzes emotions and sentiments across the crypto market.
          It gathers data from multiple sources including volatility (25%), market momentum/volume (25%),
          social media sentiment (15%), surveys (15%), Bitcoin dominance (10%), and Google Trends (10%).
          When the index shows extreme fear, it may indicate a buying opportunity. When it shows
          extreme greed, the market may be due for a correction.
        </p>
      </div>
    </div>
  );
}
